import { useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  GetPropertyTitleOwnerQuery,
  PropertyTitleOwnerOutput,
  PropertyTitleSearchAddressInput,
  useGetPropertyTitleOwnerQuery,
  useStartPropertyTitleSearchMutation,
} from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Heading } from '@spark-web/heading';
import { HomeIcon } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import EligibilityStepHeader from './EligibilityStepHeader';

const TITLE_SEARCH_POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const TERMINAL_STATUSES = new Set(['PASSED', 'EXPIRED', 'CANCELLED']);

type PropertyTitleCheckProps = {
  address: string;
  rawAddress: PropertyTitleSearchAddressInput;
  trackingId: string;
  productName: string;
  onContinue: (owners: PropertyTitleOwnerOutput[]) => void;
  onContinueToApplication: () => void;
  onBack: () => void;
};

function PropertyTitleCheck({
  address,
  rawAddress,
  trackingId,
  productName,
  onContinue,
  onContinueToApplication,
  onBack,
}: PropertyTitleCheckProps) {
  const [searchStarted, setSearchStarted] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const timedOutRef = useRef(false);

  const { mutate: startSearch } = useStartPropertyTitleSearchMutation({
    onSuccess: () => setSearchStarted(true),
    onError: () => setSearchFailed(true),
  });

  useEffect(() => {
    startSearch({ input: { trackingId, address: rawAddress } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cap polling at 3 minutes
  useEffect(() => {
    const timer = setTimeout(() => {
      timedOutRef.current = true;
      setTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  const { data: titleResult } = useGetPropertyTitleOwnerQuery(
    { input: { trackingId } },
    {
      enabled: searchStarted,
      select: (data: GetPropertyTitleOwnerQuery) => data.getPropertyTitleOwner,
      refetchInterval: data => {
        if (timedOutRef.current) return false;
        if (!data || !TERMINAL_STATUSES.has(data.searchStatus)) return TITLE_SEARCH_POLL_INTERVAL_MS;
        return false;
      },
    },
  );

  const isTerminal = titleResult !== undefined && TERMINAL_STATUSES.has(titleResult.searchStatus);
  const hasOwners = (titleResult?.owners?.length ?? 0) > 0;
  const isPassed = isTerminal && titleResult?.searchStatus === 'PASSED' && hasOwners;
  const showFallback = timedOut || searchFailed || (isTerminal && !hasOwners);

  useEffect(() => {
    if (isPassed) {
      onContinue(titleResult.owners as PropertyTitleOwnerOutput[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassed]);

  useEffect(() => {
    if (!showFallback) return;
    const reason = searchFailed ? 'search_failed' : timedOut ? 'timed_out' : 'no_owners_found';
    datadogRum.addAction('LandOwner Title Check Unavailable', { trackingId, reason });
  }, [showFallback]);

  return (
    <Stack padding="xlarge" gap="xlarge">
      <EligibilityStepHeader currentStep={3} totalSteps={5} productName={productName} />

      <Stack gap="xlarge">
        <Heading level="1">Who&apos;s on the property title?</Heading>

        <Text size="standard">
          Before we can verify your household income, we need to confirm who&apos;s the owner of the property on the
          installation address. We do this by checking the Certificate of title through the land registry.
        </Text>

        <Box borderRadius="medium" background="surfaceMuted" borderWidth="standard" border="standard">
          <Box display="flex" alignItems="center" gap="medium" padding="large">
            <Box flexShrink={0}>
              <HomeIcon size="small" tone="primary" />
            </Box>
            <Stack gap="medium">
              <Text weight="semibold" size="standard">
                Installation address
              </Text>
              <Text size="standard">{address}</Text>
            </Stack>
          </Box>

          {!showFallback && (
            <>
              <Divider />
              <Box display="flex" flexDirection="column" alignItems="center" gap="medium" padding="xlarge">
                <Spinner tone="positive" />
                <Text size="standard" align="center">
                  Checking with NSW Land Registry Services
                </Text>
              </Box>
            </>
          )}
        </Box>

        {showFallback && (
          <>
            <Alert tone="caution" heading="We're unable to retrieve your land title owners.">
              Our team will be in touch to verify property ownership before your application can be approved.
            </Alert>

            <Button tone="primary" onClick={onContinueToApplication}>
              Continue to application
            </Button>

            <Box alignSelf="start">
              <Button prominence="none" tone="neutral" onClick={onBack}>
                ← Back
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  );
}

export default PropertyTitleCheck;
